import { useEffect, useState } from "react";
import { t } from "../i18n";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Sheet } from "./Sheet";
import { PersonPicker, PlaceInput } from "./Pickers";
import { TipoSegment } from "./InteractionSheet";
import { toLocalInput, fromLocalInput } from "../lib/data";

const blank = (init = {}) => {
  const inicio = init.inicio ? new Date(init.inicio) : new Date(Date.now() + 3600000);
  inicio.setMinutes(0, 0, 0);
  const fin = init.fin ? new Date(init.fin) : new Date(inicio.getTime() + 3600000);
  return { person_ids: init.person_ids || [], inicio: toLocalInput(inicio.toISOString()), fin: toLocalInput(fin.toISOString()),
    lugar: init.lugar || { nombre: "", ciudad: "", pais: "" }, tipo: init.tipo || "encuentro", notas: init.notas || "" };
};

export function AppointmentSheet({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState(blank());
  const [conflictos, setConflictos] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setF(blank(initial || {})); setConflictos(null); } }, [open, initial]);

  const payload = () => ({
    id: initial?.id || null, person_ids: f.person_ids, inicio: fromLocalInput(f.inicio), fin: fromLocalInput(f.fin), tipo: f.tipo,
    lugar: { nombre: f.lugar.nombre || null, ciudad: f.lugar.ciudad || null, pais: f.lugar.pais || null, lat: null, lng: null }, notas: f.notas || null,
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = initial?.id ? await api.put(`/appointments/${initial.id}`, payload()) : await api.post("/appointments", payload());
      toast.success(initial?.id ? t("appt.updated") : t("appt.saved"));
      onSaved?.(data); onClose();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.person_ids.length === 0) return toast.error(t("interaction.selectPerson"));
    if (f.fin && f.fin < f.inicio) return toast.error(t("appt.endAfterStart"));
    setSaving(true);
    try {
      const { data } = await api.post("/appointments/check-conflicts", payload());
      if (data.conflictos.length > 0) { setConflictos(data.conflictos); setSaving(false); return; }
    } catch (err) { toast.error(errorMessage(err)); setSaving(false); return; }
    await save();
  };

  return (
    <Sheet open={open} onClose={onClose} title={initial?.id ? t("appt.edit") : t("appt.new")} testId="appointment-sheet">
      <form onSubmit={submit} className="space-y-4" data-testid="appointment-form">
        <PersonPicker value={f.person_ids} onChange={(v) => setF({ ...f, person_ids: v })} />
        <div><label className="label">{t("common.type")}</label><TipoSegment value={f.tipo} onChange={(v) => setF({ ...f, tipo: v })} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">{t("appt.start")}</label><input data-testid="appointment-inicio" type="datetime-local" className="field" value={f.inicio} onChange={(e) => {
            const ini = e.target.value; const dur = new Date(f.fin) - new Date(f.inicio);
            setF({ ...f, inicio: ini, fin: ini ? toLocalInput(new Date(new Date(ini).getTime() + (dur > 0 ? dur : 3600000)).toISOString()) : f.fin });
          }} required /></div>
          <div><label className="label">{t("appt.end")}</label><input data-testid="appointment-fin" type="datetime-local" className="field" min={f.inicio} value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} />
            {f.fin && f.inicio && f.fin.slice(0, 10) !== f.inicio.slice(0, 10) && <p data-testid="appointment-multidia" className="text-[11px] text-[#f3c649] mt-1">{t("appt.multiDay")}</p>}</div>
        </div>
        <PlaceInput value={f.lugar} onChange={(v) => setF({ ...f, lugar: v })} />
        <div><label className="label">{t("common.notesEnc")}</label><textarea data-testid="appointment-notas" className="field min-h-[60px]" value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} /></div>

        {conflictos ? (
          <div data-testid="conflict-warning" className="rounded-xl border border-[#f43f5e]/50 bg-[#f43f5e]/10 p-4 space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium text-[#fb7185]"><AlertTriangle size={16} /> {t("appt.conflicts")}</p>
            <ul className="text-sm space-y-1">{conflictos.map((c, i) => <li key={i} data-testid={`conflict-${c.tipo}`}>• {c.mensaje}</li>)}</ul>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button type="button" data-testid="conflict-change-btn" onClick={() => setConflictos(null)} className="btn-ghost text-sm">{t("common.change")}</button>
              <button type="button" data-testid="conflict-save-anyway-btn" onClick={save} disabled={saving} className="rounded-full bg-[#f43f5e] text-white py-3 text-sm font-semibold">{t("appt.saveAnyway")}</button>
            </div>
          </div>
        ) : (
          <button data-testid="appointment-save-btn" className="btn-primary" disabled={saving}>{saving ? t("common.saving") : t("common.save")}</button>
        )}
      </form>
    </Sheet>
  );
}
